import { on } from '@ngrx/store';
import { mergeSurveyPatch, patchSurveyInList } from '@ps/survey-utils';
import { ImpersonationActions } from '@ps/auth-data-access';
import { SURVEY_STATUS } from '@ps/survey-kernel';

import {
  ManageSurveyActions,
  ManageSurveyApiActions,
} from './manage-survey.actions';
import {
  SurveyListActions,
  SurveyListApiActions,
} from '@ps/survey-data-access';
import {
  INITIAL_MANAGE_SURVEY_STATE,
  ManageSurveyState,
} from './manage-survey.model';

export const CORE_SURVEY_HANDLERS = [
  on(
    ManageSurveyActions.loadSurveyDetails,
    ManageSurveyActions.loadFullSurveyDetails,
    (state): ManageSurveyState => ({
      ...state,
      loading: true,
      error: null,
      // A fresh root-survey load starts a new session — any previously
      // selected sibling must not keep shadowing it once this resolves.
      childSurvey: null,
      childSurveyLoading: false,
      childSurveyError: null,
    }),
  ),

  on(
    ManageSurveyActions.resetSurveyDetails,
    (): ManageSurveyState => INITIAL_MANAGE_SURVEY_STATE,
  ),

  on(
    ManageSurveyApiActions.loadSurveySuccess,
    (state, { survey }): ManageSurveyState => ({
      ...state,
      survey,
      loading: false,
      error: null,
    }),
  ),

  on(
    ManageSurveyApiActions.loadSurveyFailure,
    (state, { error }): ManageSurveyState => ({
      ...state,
      survey: null,
      loading: false,
      error,
    }),
  ),

  on(
    ManageSurveyActions.loadChildSurveyDetails,
    (state): ManageSurveyState => ({
      ...state,
      childSurveyLoading: true,
      childSurveyError: null,
    }),
  ),

  on(
    ManageSurveyApiActions.loadChildSurveySuccess,
    (state, { survey }): ManageSurveyState => ({
      ...state,
      childSurvey: survey,
      childSurveyLoading: false,
      childSurveyError: null,
    }),
  ),

  on(
    ManageSurveyApiActions.loadChildSurveyFailure,
    (state, { error }): ManageSurveyState => ({
      ...state,
      childSurvey: null,
      childSurveyLoading: false,
      childSurveyError: error,
    }),
  ),

  on(
    ImpersonationActions.contextSettled,
    (): ManageSurveyState => INITIAL_MANAGE_SURVEY_STATE,
  ),

  on(
    ManageSurveyActions.updateSurvey,
    SurveyListActions.updateSurvey,
    (state, { surveyId, survey }): ManageSurveyState => ({
      ...state,
      survey:
        state.survey?.id === surveyId
          ? mergeSurveyPatch(state.survey, survey)
          : state.survey,
      // Traffic channels are surveys too; patch a matching channel so the
      // traffic tab reflects live socket updates.
      trafficChannels: patchSurveyInList(
        state.trafficChannels,
        surveyId,
        survey,
      ),
      error: null,
    }),
  ),

  on(
    SurveyListApiActions.updateSurveyStatusSuccess,
    (state, { surveyId, status }): ManageSurveyState => ({
      ...state,
      survey:
        state.survey?.id === surveyId
          ? mergeSurveyPatch(state.survey, { status })
          : state.survey,
    }),
  ),

  on(
    ManageSurveyActions.cloneSurvey,
    (state): ManageSurveyState => ({
      ...state,
      cloning: true,
      cloneError: null,
      clonedSurveyId: null,
    }),
  ),

  on(
    ManageSurveyApiActions.cloneSurveySuccess,
    (state, { survey }): ManageSurveyState => ({
      ...state,
      cloning: false,
      cloneError: null,
      clonedSurveyId: survey.id,
    }),
  ),

  on(
    ManageSurveyApiActions.cloneSurveyFailure,
    (state, { error }): ManageSurveyState => ({
      ...state,
      cloning: false,
      cloneError: error,
      clonedSurveyId: null,
    }),
  ),

  on(
    SurveyListApiActions.invoiceSurveysSuccess,
    (state, { surveyIds }): ManageSurveyState => ({
      ...state,
      survey:
        state.survey && surveyIds.includes(state.survey.id)
          ? mergeSurveyPatch(state.survey, { status: SURVEY_STATUS.INVOICED })
          : state.survey,
    }),
  ),
];
